"use client";
import { HugeiconsIcon } from "@hugeicons/react";
import { User02Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@workspace/frontend-ui";
import { usePathname } from "next/navigation";
import type { FC } from "react";
import { useEffect, useState } from "react";
import LoyaltyHeader from "./LoyaltyHeader";
import ProfileGreeting from "./ProfileGreeting";
import ProfileLogoutItem from "./ProfileLogoutItem";
import ProfileMenuItem from "./ProfileMenuItem";
import type { Loyalty, ProfileMenuEntry } from "./types";

type Props = {
  greeting: string;
  loyalty: Loyalty;
  badges: Record<string, number>;
  items: ProfileMenuEntry[];
  tierLabel: string;
  multiplierLabel: string;
  tooltip: string;
  isLoyaltyHidden: boolean;
};

const ProfileView: FC<Props> = ({
  greeting,
  loyalty,
  badges,
  items,
  tierLabel,
  multiplierLabel,
  tooltip,
  isLoyaltyHidden,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const menuItems = items.filter((item) => item.action !== "logout");
  const logoutItem = items.find((item) => item.action === "logout");

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon-xl"
          className="size-10 md:size-10 rounded-xl border-border bg-accent text-muted-foreground"
          aria-label={tooltip || "Open profile menu"}
        >
          <HugeiconsIcon icon={User02Icon} size={19} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        collisionPadding={0}
        className="w-72 rounded-lg bg-background max-sm:w-screen max-sm:rounded-t-none"
      >
        {greeting && <ProfileGreeting greeting={greeting} />}
        {!isLoyaltyHidden && (
          <LoyaltyHeader
            loyalty={loyalty}
            tierLabel={tierLabel}
            multiplierLabel={multiplierLabel}
          />
        )}
        <DropdownMenuSeparator />
        {menuItems.map((item) => (
          <ProfileMenuItem
            key={item.slug}
            item={item}
            badge={badges[item.slug ?? ""]}
          />
        ))}
        {logoutItem && <ProfileLogoutItem item={logoutItem} />}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProfileView;
